import React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from './Button';
import { ChevronLeftIcon } from './icons';

interface PageHeaderProps {
  /** Page title (rendered as h1). */
  title: string;
  /** Optional subtitle below the title. */
  subtitle?: string;
  /** When provided, renders a "Back" button that navigates to this path. */
  backTo?: string;
  /** Optional action element (e.g. a Button) rendered on the right side. */
  action?: React.ReactNode;
  /** Additional CSS classes on the wrapper. */
  className?: string;
}

/**
 * Standardised page header used across all pages.
 *
 * Three layout variants, selected automatically:
 * - **Plain**: title + subtitle
 * - **With action**: title + subtitle + right-aligned action element
 * - **With back button**: back link + title (+ optional subtitle and action)
 */
export function PageHeader({ title, subtitle, backTo, action, className }: Readonly<PageHeaderProps>) {
  const navigate = useNavigate();

  return (
    <div className={cn('space-y-1', className)}>
      {backTo && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(backTo)}
          className="mb-1"
        >
          <ChevronLeftIcon className="h-4 w-4 mr-1" />
          Back
        </Button>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">{title}</h1>
          {subtitle && <p className="text-muted-foreground">{subtitle}</p>}
        </div>
        {action && <div className="flex-shrink-0">{action}</div>}
      </div>
    </div>
  );
}
